<template>
    <button
        v-if="isLogo"
        type="button"
        :class="[logoClass, 'navbar-home-link']"
        @click="$emit('navigate-home')"
    >
        <img
            v-if="logoUrl"
            :src="logoUrl"
            :height="logoHeight"
            :alt="altText"
        />
        <InlineSvg
            v-else
            :src="logoPlaceholder"
            class="navbar-logo-placeholder"
            :aria-label="altText"
            :style="{ height: `${logoHeight}px` }"
        />
    </button>
    <button
        v-else-if="isName"
        type="button"
        class="navbar-id navbar-home-link"
        :style="nameStyle"
        @click="$emit('navigate-home')"
    >
        {{ userName }}
    </button>
</template>

<script>
import InlineSvg from "vue-inline-svg";
import logoPlaceholder from "@/images/portfolio-logo-placeholder.svg";
import { mapGetters } from "vuex";

export default {
    name: "PortfolioNavigationLogo",
    emits: ["navigate-home"],
    components: { InlineSvg },
    props: {
        logo: {
            type: Object,
            default: null,
        },
        userName: {
            type: String,
            default: "",
        },
        logoClass: {
            type: String,
            default: "navbar-logo",
        },
    },
    computed: {
        ...mapGetters("design", ["colorPalette"]),
        logoPlaceholder() {
            return logoPlaceholder;
        },
        logoUrl() {
            const media = this.logo?.media;
            if (media?.croppedImageUrl) return media.croppedImageUrl;
            if (media?.url) return media.url;
            return null;
        },
        logoHeight() {
            const height = Number(this.logo?.media?.height);
            return Number.isFinite(height) && height > 0 ? height : 32;
        },
        isLogo() {
            return this.logo?.mode === "Logo";
        },
        isName() {
            return this.logo?.mode === "Name";
        },
        altText() {
            return this.logo?.media?.altTag ? this.logo.media.altTag : 'Portfolio logo'
        },
        nameStyle() {
            if (!this.isName) return null;

            const fontSettings = this.logo?.fontSettings;
            const textColor = this.logo?.textColor;
            if (!fontSettings && !textColor) return null;

            const style = {};

            if (fontSettings?.font) style.fontFamily = fontSettings.font;
            if (fontSettings?.fontWeight) style.fontWeight = fontSettings.fontWeight;
            if (fontSettings?.fontStyle) style.fontStyle = fontSettings.fontStyle;
            if (fontSettings?.size) style.fontSize = `${fontSettings.size}px`;
            if (fontSettings?.lineHeight) style.lineHeight = `${fontSettings.lineHeight}px`;

            const resolvedColor = this.resolveTextColor(textColor);
            if (resolvedColor) style.color = resolvedColor;

            return Object.keys(style).length ? style : null;
        },
    },
    methods: {
        resolveTextColor(colorStyle) {
            if (!colorStyle) return null;

            const colorData = colorStyle?.text?.idle;
            if (Number.isFinite(colorData?.paletteColorIndex)) {
                return this.colorPalette?.colors?.[colorData.paletteColorIndex]
                    || colorData?.color
                    || colorStyle?.primaryColor
                    || null;
            }

            return colorData?.color || colorStyle?.primaryColor || null;
        },
    },
};
</script>

<style scoped lang="scss">
.navbar-home-link {
    appearance: none;
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    outline: none;
    display: flex;
    align-items: center;
    justify-content: center;
    &:focus {
        outline: none
    }
}

.navbar-logo-placeholder {
    width: auto;
    max-width: 256px;
    color: var(--hero-nav-button-text-color);
}
</style>
